import React, { useCallback } from 'react';
import {
  Row,
  SPWCancelButton,
  SPWDraggableResizableDialog,
  SPWInput,
  SPWSaveButton,
} from 'spw-shared/globalComponents';
import { ClientNamesStatus } from 'spw-components/dialogs/spaces/styles';
import CurrencyInput from 'react-currency-input-field';
import { useSelector } from 'react-redux';
import { selectClientFacingNamesAreVisible } from 'spw-redux/modules/space_types/selectors';
import { AvgCardPopoverContent } from 'spw-projects/components/styles';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { colors } from 'spw-theme';
import { useAvgFormLogic } from 'spw-shared/hooks/use-avg-form-logic';

type Props = {
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  subject: string;
  spaceTypeName: string;
  texts: string[];
  onSave: (value: number, callback: () => void) => void;
  max: number;
  min: number;
  allowDecimals?: boolean;
};

const AvgCardPopover = ({
  anchorEl,
  onClose,
  subject,
  spaceTypeName,
  texts,
  onSave,
  max,
  min,
  allowDecimals = false,
}: Props) => {
  const { getUnit, convertToOriginalUnit } = useUnitConversion();
  const clientFacingNamesAreVisible = useSelector(
    selectClientFacingNamesAreVisible
  );

  const { value, floatValue, handleValueChanged } = useAvgFormLogic({ max });

  const handleSave = () => {
    const valueToUpdate =
      subject === 'USF'
        ? convertToOriginalUnit(floatValue, { unitType: 'area' })
        : floatValue;
    onSave(valueToUpdate, onClose);
  };

  const title = subject === 'USF' ? getUnit('area') : subject;

  const Input = useCallback(
    (props: Omit<React.ComponentPropsWithRef<'input'>, 'color' | 'size'>) => (
      <SPWInput {...props} variant="outlined" label={title.toUpperCase()} />
    ),
    [title]
  );

  return (
    <SPWDraggableResizableDialog
      anchorEl={anchorEl}
      onClose={onClose}
      width={370}
      minHeight={463}
      disableResize
      ContentWrapper={AvgCardPopoverContent}
      title={
        <div className="header">
          <Row justifyContent="space-between" alignItems="center">
            <div className="title">
              {subject === 'USF' ? getUnit('area') : subject}
            </div>
            <ClientNamesStatus
              clientFacingNamesAreVisible={clientFacingNamesAreVisible}
            />
          </Row>
        </div>
      }
    >
      <div className="content">
        <div className="space-type-name">{spaceTypeName}</div>
        <div className="warning">CAUTION</div>
        <div className="description">
          {texts.map((t) => (
            <p key={t}>{t}</p>
          ))}
        </div>
        <CurrencyInput
          min={min}
          max={max}
          allowNegativeValue={false}
          value={value}
          onValueChange={handleValueChanged}
          allowDecimals={allowDecimals}
          customInput={Input}
        />
      </div>
      <div className="actions">
        <SPWSaveButton onClick={handleSave} />
        <SPWCancelButton onClick={onClose} customColor={colors.black} />
      </div>
    </SPWDraggableResizableDialog>
  );
};

export default AvgCardPopover;
